import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { QRCodeSVG } from 'qrcode.react';

import Card from '../../../components/Card/Card';
import Text from '../../../components/Text/Text';
import Input from '../../../components/Input/Input';
import pathMyProfile from '../../../thunks/profile/patchProfile';
import Alert from '../../../components/Alert/Alert';
import alertType from '../../../common/alertType';
import ImagePicker from '../../../components/ImagePicker/ImagePicker';
import { MEDIA_URL, METHOD_POST } from '../../../constants/api';
import patchProfileAvatar from '../../../thunks/profile/patchProfileAvatar';
import Button from '../../../components/Button/Button';
import { authSet } from '../../../utils/request';
import getMyProfile from '../../../thunks/profile/getMyProfile';

const ProfileEdit = () => {
  const dispatch = useDispatch();
  const {
    user = {},
    pathProfile: { error: patchError } = {}
  } = useSelector((state) => ({
    user: state?.getMyProfile?.user,
    pathProfile: state?.pathProfile,
  }), shallowEqual);

  const isTwoFactorFeatureEnabled = process.env.REACT_APP_ENABLE_2FA !== 'false';
  const [userImage, setUserImage] = useState(user?.avatar ? `${MEDIA_URL}/${user?.avatar}` : null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState(null);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState(null);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [recoveryActionCode, setRecoveryActionCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [faceLoginLoading, setFaceLoginLoading] = useState(false);
  const [faceLoginError, setFaceLoginError] = useState(null);
  const [faceLoginSuccess, setFaceLoginSuccess] = useState(null);
  const [faceLoginPreview, setFaceLoginPreview] = useState(null);
  const faceLoginInputRef = useRef(null);
  const faceLoginVideoRef = useRef(null);
  const faceLoginCanvasRef = useRef(null);
  const faceLoginStreamRef = useRef(null);
  const [isFaceCameraVisible, setIsFaceCameraVisible] = useState(false);
  const [isFaceCameraLoading, setIsFaceCameraLoading] = useState(false);
  const {
    values: {
      email,
      name,
      login,
      lastName,
    },
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    errors,
    touched,
    setFieldValue
  } = useFormik({
    initialValues: {
      email: user?.email || '',
      name: user?.name || '',
      login: user?.login || '',
      lastName: user.last_name || '',
      profileImage: null
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values.profileImage) {
        dispatch(patchProfileAvatar({ image: values.profileImage }));
      }
      dispatch(pathMyProfile({
        name: values?.name,
        last_name: values?.lastName,
        login: values?.login
      }));
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Required').min(3),
      lastName: Yup.string().required('Required').min(3),
      login: Yup.string().required('Required').min(3),
    })
  });

  const onImageChange = ({ target }) => {
    const url = URL.createObjectURL(target.files[0]);
    setUserImage(url);
    setFieldValue('profileImage', target.files[0]);
  };

  const onImageClear = () => {
    setUserImage(`${MEDIA_URL}/${user?.avatar}` || null);
    setFieldValue('profileImage', null);
  };

  const clearTwoFactorMessages = () => {
    setTwoFactorError(null);
    setTwoFactorSuccess(null);
  };

  const clearFaceLoginMessages = () => {
    setFaceLoginError(null);
    setFaceLoginSuccess(null);
  };

  const stopFaceCamera = () => {
    const currentStream = faceLoginStreamRef.current;

    if (!currentStream) {
      return;
    }

    currentStream.getTracks().forEach((track) => track.stop());
    faceLoginStreamRef.current = null;

    if (faceLoginVideoRef.current) {
      faceLoginVideoRef.current.srcObject = null;
    }
  };

  const getReadableCameraError = (error) => {
    const message = error?.message || String(error || '');

    if (/permission|denied|notallowed/i.test(message)) {
      return 'Allow camera access in the browser and try again';
    }

    if (/secure|https/i.test(message)) {
      return 'Camera access requires a secure origin (HTTPS or localhost)';
    }

    if (/camera|device|video/i.test(message)) {
      return 'Unable to start the camera. Try another camera or upload a photo.';
    }

    return message || 'Camera access is unavailable';
  };

  const startTwoFactorSetup = async () => {
    setTwoFactorLoading(true);
    clearTwoFactorMessages();

    try {
      const response = await authSet(METHOD_POST, '/2fa/setup', {});
      setTwoFactorSetup(response);
      setRecoveryCodes([]);
    } catch (error) {
      setTwoFactorError(error.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const confirmTwoFactorSetup = async () => {
    setTwoFactorLoading(true);
    clearTwoFactorMessages();

    try {
      const response = await authSet(METHOD_POST, '/2fa/confirm', { code: twoFactorCode });
      setRecoveryCodes(response.recovery_codes || []);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setTwoFactorSuccess(response.message);
      dispatch(getMyProfile());
    } catch (error) {
      setTwoFactorError(error.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    setTwoFactorLoading(true);
    clearTwoFactorMessages();

    try {
      const response = await authSet(METHOD_POST, '/2fa/disable', { code: disableCode });
      setDisableCode('');
      setRecoveryCodes([]);
      setTwoFactorSetup(null);
      setTwoFactorSuccess(response.message);
      dispatch(getMyProfile());
    } catch (error) {
      setTwoFactorError(error.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const regenerateRecoveryCodes = async () => {
    setTwoFactorLoading(true);
    clearTwoFactorMessages();

    try {
      const response = await authSet(METHOD_POST, '/2fa/recovery-codes/regenerate', {
        code: recoveryActionCode
      });
      setRecoveryCodes(response.recovery_codes || []);
      setRecoveryActionCode('');
      setTwoFactorSuccess(response.message);
    } catch (error) {
      setTwoFactorError(error.message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const setupFaceLogin = async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);

    setFaceLoginLoading(true);
    clearFaceLoginMessages();

    try {
      const response = await authSet(METHOD_POST, '/face-login/setup', formData);
      setFaceLoginSuccess(response.message);
      dispatch(getMyProfile());
    } catch (error) {
      setFaceLoginError(error.message);
    } finally {
      setFaceLoginLoading(false);
    }
  };

  const disableFaceLogin = async () => {
    setFaceLoginLoading(true);
    clearFaceLoginMessages();

    try {
      const response = await authSet(METHOD_POST, '/face-login/disable', {});
      setFaceLoginPreview(null);
      setIsFaceCameraVisible(false);
      stopFaceCamera();
      setFaceLoginSuccess(response.message);
      dispatch(getMyProfile());
    } catch (error) {
      setFaceLoginError(error.message);
    } finally {
      setFaceLoginLoading(false);
    }
  };

  const onFaceLoginReferenceChange = async (event) => {
    const inputElement = event.currentTarget;
    const imageFile = inputElement.files?.[0];

    if (!imageFile) {
      return;
    }

    setFaceLoginPreview(URL.createObjectURL(imageFile));
    await setupFaceLogin(imageFile);
    inputElement.value = '';
  };

  const startFaceCamera = async () => {
    if (faceLoginStreamRef.current || !faceLoginVideoRef.current) {
      return;
    }

    clearFaceLoginMessages();
    setIsFaceCameraLoading(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
        },
        audio: false,
      });

      faceLoginStreamRef.current = stream;
      faceLoginVideoRef.current.srcObject = stream;
      await faceLoginVideoRef.current.play();
    } catch (error) {
      setFaceLoginError(getReadableCameraError(error));
      setIsFaceCameraVisible(false);
    } finally {
      setIsFaceCameraLoading(false);
    }
  };

  const captureFaceLoginReference = async () => {
    const videoElement = faceLoginVideoRef.current;
    const canvasElement = faceLoginCanvasRef.current;

    if (!videoElement || !canvasElement) {
      return;
    }

    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      setFaceLoginError('Camera is not ready yet');
      return;
    }

    canvasElement.width = videoElement.videoWidth;
    canvasElement.height = videoElement.videoHeight;

    const context = canvasElement.getContext('2d');
    context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

    const previewUrl = canvasElement.toDataURL('image/jpeg', 0.92);
    setFaceLoginPreview(previewUrl);

    canvasElement.toBlob(async (blob) => {
      if (!blob) {
        setFaceLoginError('Unable to capture the current frame');
        return;
      }

      const imageFile = new File([blob], 'face-reference.jpg', { type: 'image/jpeg' });
      await setupFaceLogin(imageFile);
      setIsFaceCameraVisible(false);
      stopFaceCamera();
    }, 'image/jpeg', 0.92);
  };

  useEffect(() => {
    if (!isFaceCameraVisible) {
      stopFaceCamera();
      return undefined;
    }

    startFaceCamera();

    return () => {
      stopFaceCamera();
    };
  }, [isFaceCameraVisible]);

  return (
    <>
      <div>
        <Card
          headerTitle="Profile Details"
          showHeader
          styleBodyContainer="p-[30px]"
          footer={(
            <div className="flex flex-row justify-end">
              <Button
                text="Save Changes"
                disabled={!isValid}
                onClick={handleSubmit}
              />
            </div>
          )}
        >
          <div>
            <div className="flex flex-row items-start mb-[23px]">
              <div className="flex-1">
                <Text text="Avatar:" textStyle="text-[14px]" />
              </div>
              <div className="flex flex-row flex-[2]">
                <ImagePicker
                  defaultImage={userImage}
                  onImageChange={onImageChange}
                  onClear={onImageClear}
                />
              </div>
            </div>
            <div className="flex items-center mb-[23px]">
              <div className="flex-1">
                <Text text="Full name:" textStyle="text-[14px]" required />
              </div>
              <div className="flex flex-row flex-[2]">
                <div className="flex-1 mr-[20px]">
                  <Input
                    type="text"
                    name="name"
                    value={name}
                    placeholder="Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errorText={touched?.name && errors?.name}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    type="text"
                    name="lastName"
                    value={lastName}
                    placeholder="Last name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errorText={touched?.lastName && errors?.lastName}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center mb-[23px]">
              <div className="flex-1">
                <Text text="Email:" textStyle="text-[14px]" />
              </div>
              <div className="flex-[2]">
                <Input
                  type="text"
                  value={email}
                  placeholder="Email"
                  disabled
                  onChange={() => {}}
                />
              </div>
            </div>
            <div className="flex items-center mb-[23px]">
              <div className="flex-1">
                <Text text="Login:" textStyle="text-[14px]" required />
              </div>
              <div className="flex-[2]">
                <Input
                  type="text"
                  name="login"
                  value={login}
                  placeholder="login"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  errorText={touched?.login && errors?.login}
                />
              </div>
            </div>
            {patchError && (
              <Alert
                text={patchError}
                type={alertType.warning}
              />
            )}
          </div>
        </Card>
      </div>

      {isTwoFactorFeatureEnabled && (
        <div className="mt-[24px]">
          <Card
            headerTitle="Google Authenticator"
            showHeader
            styleBodyContainer="p-[30px]"
          >
            <div className="flex flex-col gap-[18px]">
              <Text
                text={user?.two_factor_enabled
                  ? 'Two-factor authentication is enabled for your account.'
                  : 'Protect this account with Google Authenticator-compatible TOTP codes.'}
                textStyle="text-[14px]"
              />

              {!user?.two_factor_enabled && !twoFactorSetup && (
                <div className="flex justify-start mb-[40px]">
                  <Button
                    text="Set Up Google Authenticator"
                    disabled={twoFactorLoading}
                    onClick={startTwoFactorSetup}
                  />
                </div>
              )}

              {!user?.two_factor_enabled && twoFactorSetup && (
                <div className="flex flex-col gap-[14px]">
                  <Text
                    text="Google Authenticator setup"
                    bold
                    textStyle="text-[15px]"
                  />
                  <Text
                    text={'Scan the generated otpauth URL with a compatible app or add the account manually '
                      + 'using the key below.'}
                    textStyle="text-[13px]"
                  />
                  {twoFactorSetup?.otpauth_url && (
                    <div className="flex justify-center">
                      <div className="bg-white rounded-[10px] p-[12px] shadowDefault">
                        <QRCodeSVG
                          value={twoFactorSetup.otpauth_url}
                          size={180}
                          includeMargin
                        />
                      </div>
                    </div>
                  )}
                  <Input
                    value={twoFactorSetup.account_name || ''}
                    placeholder="Account"
                    disabled
                    onChange={() => {}}
                  />
                  <Input
                    value={twoFactorSetup.manual_entry_key || ''}
                    placeholder="Manual entry key"
                    disabled
                    onChange={() => {}}
                  />
                  <Input
                    value={twoFactorSetup.otpauth_url || ''}
                    placeholder="otpauth URL"
                    disabled
                    onChange={() => {}}
                  />
                  <Input
                    type="text"
                    value={twoFactorCode}
                    placeholder="Enter 6-digit code"
                    onChange={({ target }) => setTwoFactorCode(target.value)}
                  />
                  <div className="flex flex-row justify-between mb-[20px]">
                    <Button
                      text="Cancel"
                      disabled={twoFactorLoading}
                      onClick={() => {
                        setTwoFactorSetup(null);
                        setTwoFactorCode('');
                        clearTwoFactorMessages();
                      }}
                    />
                    <Button
                      text="Confirm 2FA"
                      disabled={!twoFactorCode || twoFactorLoading}
                      onClick={confirmTwoFactorSetup}
                    />
                  </div>
                </div>
              )}

              {user?.two_factor_enabled && (
                <div className="flex flex-col gap-[14px]">
                  <Input
                    type="text"
                    value={recoveryActionCode}
                    placeholder="Current code to regenerate recovery codes"
                    onChange={({ target }) => setRecoveryActionCode(target.value)}
                  />
                  <div className="flex justify-start">
                    <Button
                      text="Regenerate Recovery Codes"
                      disabled={!recoveryActionCode || twoFactorLoading}
                      onClick={regenerateRecoveryCodes}
                    />
                  </div>
                  <Input
                    type="text"
                    value={disableCode}
                    placeholder="Current Google Authenticator code or recovery code to disable"
                    onChange={({ target }) => setDisableCode(target.value)}
                  />
                  <div className="flex justify-start">
                    <Button
                      text="Disable 2FA"
                      disabled={!disableCode || twoFactorLoading}
                      onClick={disableTwoFactor}
                    />
                  </div>
                </div>
              )}

              {!!recoveryCodes.length && (
                <div className="bg-background-main rounded-[6px] p-[16px]">
                  <Text
                    text="Recovery codes"
                    bold
                    textStyle="text-[14px]"
                  />
                  <Text
                    text="Save these codes. Each one can be used only once."
                    textStyle="text-[12px]"
                    muted
                  />
                  <div className="grid grid-cols-2 gap-[8px] mt-[12px]">
                    {recoveryCodes.map((code) => (
                      <div
                        key={code}
                        className="font-mono text-[13px] bg-white rounded-[6px] px-[12px] py-[8px]"
                      >
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(twoFactorError || twoFactorSuccess) && (
                <Alert
                  text={twoFactorError || twoFactorSuccess}
                  type={twoFactorError ? alertType.warning : alertType.info}
                />
              )}
            </div>
          </Card>
        </div>
      )}

      <div className="mt-[24px]">
        <Card
          headerTitle="Amazon Rekognition Login"
          showHeader
          styleBodyContainer="p-[30px]"
        >
          <div className="flex flex-col gap-[18px] mb-[20px]">
            <Text
              text={user?.face_login_enabled
                ? 'Face login is enabled. Upload a new reference photo any time if you need to refresh it.'
                : 'Upload a clear reference photo. Amazon Rekognition will compare it to the login selfie.'}
              textStyle="text-[14px]"
            />
            <input
              ref={faceLoginInputRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              className="hidden"
              onChange={onFaceLoginReferenceChange}
            />

            <div className="flex flex-row gap-[12px] flex-wrap">
              <Button
                text={user?.face_login_enabled ? 'Replace Reference Photo' : 'Upload Reference Photo'}
                disabled={faceLoginLoading}
                onClick={() => faceLoginInputRef.current?.click()}
              />
              <Button
                text={isFaceCameraVisible ? 'Hide Camera' : 'Capture with Camera'}
                disabled={faceLoginLoading}
                onClick={() => {
                  clearFaceLoginMessages();
                  setIsFaceCameraVisible((currentValue) => !currentValue);
                }}
              />
              {user?.face_login_enabled && (
                <Button
                  text="Disable Face Login"
                  disabled={faceLoginLoading}
                  onClick={disableFaceLogin}
                />
              )}
            </div>

            {isFaceCameraVisible ? (
              <div className="bg-background-main rounded-[10px] p-[18px] flex flex-col gap-[14px]">
                <Text
                  text="Camera reference capture"
                  bold
                  textStyle="text-[14px]"
                />
                <div className="rounded-[10px] overflow-hidden bg-black">
                  <video
                    ref={faceLoginVideoRef}
                    className="w-full max-w-[420px] h-[280px] object-cover mx-auto"
                    autoPlay
                    muted
                    playsInline
                  />
                </div>
                <canvas ref={faceLoginCanvasRef} className="hidden" />
                <div className="flex gap-[12px] flex-wrap">
                  <Button
                    text="Capture Reference Photo"
                    disabled={faceLoginLoading || isFaceCameraLoading}
                    onClick={captureFaceLoginReference}
                  />
                  <Button
                    text="Close Camera"
                    disabled={faceLoginLoading}
                    onClick={() => setIsFaceCameraVisible(false)}
                  />
                </div>
                {isFaceCameraLoading ? (
                  <Text
                    text="Starting camera..."
                    muted
                    styleContainer="justify-center"
                  />
                ) : null}
              </div>
            ) : null}

            {faceLoginPreview ? (
              <div className="bg-background-main rounded-[10px] p-[18px] flex flex-col gap-[14px]">
                <Text
                  text="Reference photo preview"
                  bold
                  textStyle="text-[14px]"
                />
                <div className="flex justify-center">
                  <img
                    src={faceLoginPreview}
                    alt="Face login reference preview"
                    className="w-full max-w-[280px] h-[280px] object-cover rounded-[10px] shadowDefault"
                  />
                </div>
                <Input
                  value={user?.email || user?.login || ''}
                  placeholder="Reference account"
                  disabled
                  onChange={() => {}}
                />
              </div>
            ) : null}

            {user?.face_login_enabled ? (
              <Alert
                text={'Reference photo is stored. Login modal will capture a new selfie and compare it '
                  + 'with Amazon Rekognition.'}
                type={alertType.info}
              />
            ) : null}

            {(faceLoginError || faceLoginSuccess) && (
              <Alert
                text={faceLoginError || faceLoginSuccess}
                type={faceLoginError ? alertType.warning : alertType.info}
              />
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default ProfileEdit;
